/**
 * Full-page search results — used when the user hits Enter in the omnibar
 * or lands from a shared /search?q=… URL.
 */
import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Search, ArrowRight, Loader2 } from "lucide-react";
import { http } from "@/lib/api";

const TYPE_TINT = {
  news: "#1D4ED8",
  memorial: "#5B4A73",
  event: "#C47F3A",
  cause: "#B23A3A",
  world_changer: "#5C6B52",
  story: "#8A5CF0",
  organization: "#0F766E",
  housing: "#7C3AED",
  recipe: "#EA580C",
  service: "#6B7280",
};

export default function SearchResults() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const initial = params.get("q") || "";
  const [q, setQ] = useState(initial);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const query = (params.get("q") || "").trim();
    setQ(query);
    if (query.length < 2) { setData(null); return; }
    setLoading(true);
    http.get("/search", { params: { q: query, per_type: 20 } })
      .then((r) => setData(r.data))
      .catch(() => setData({ groups: [], total: 0 }))
      .finally(() => setLoading(false));
  }, [params]);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim().length < 2) return;
    setParams({ q: q.trim() });
  };

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-10" data-testid="search-page">
      <div className="mb-6">
        <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>
          <Search size={13} className="inline mr-1.5 -mt-0.5" /> SEARCH RESULTS
        </div>
        <h1 className="font-display text-4xl md:text-5xl leading-tight">Find what matters.</h1>
      </div>

      <form onSubmit={submit} className="mb-8 flex gap-2">
        <input
          data-testid="search-page-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search across the whole site…"
          className="wofy-input flex-1"
        />
        <button type="submit" className="wofy-btn-primary">
          Search
        </button>
      </form>

      {loading && (
        <div className="flex items-center gap-2 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Searching…
        </div>
      )}

      {!loading && data && data.total === 0 && (
        <div className="wofy-card text-center">
          <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>
            No matches for "<b>{q}</b>". Try broader or fewer words.
          </p>
        </div>
      )}

      {!loading && data && data.total > 0 && (
        <>
          <p className="mb-4 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
            <b>{data.total}</b> match{data.total === 1 ? "" : "es"} across <b>{data.groups.length}</b> section{data.groups.length === 1 ? "" : "s"}.
          </p>
          {data.groups.map((g) => (
            <section key={g.type} className="mb-8" data-testid={`search-results-group-${g.type}`}>
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="font-display text-2xl">
                  <span
                    className="inline-block w-2 h-2 rounded-full mr-2 -mb-0.5"
                    style={{ background: TYPE_TINT[g.type] || "#666" }}
                  />
                  {g.label}
                </h2>
                <span className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
                  {g.count} result{g.count === 1 ? "" : "s"}
                </span>
              </div>
              <ul className="divide-y" style={{ borderColor: "var(--wofy-line)" }}>
                {g.items.map((it) => (
                  <li key={`${it.type}-${it.id}`}>
                    <Link
                      to={it.url}
                      data-testid={`search-result-${it.type}-${it.id}`}
                      className="flex items-start gap-4 py-4 hover:bg-[color:var(--wofy-surface)]/60 px-3 -mx-3 rounded"
                    >
                      <div
                        className="w-16 h-16 rounded-lg overflow-hidden shrink-0 flex items-center justify-center"
                        style={{ background: (TYPE_TINT[it.type] || "#666") + "22", color: TYPE_TINT[it.type] || "#666" }}
                      >
                        {it.image ? (
                          <img src={it.image} alt="" className="w-full h-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
                        ) : (
                          <Search className="w-5 h-5" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-base font-semibold leading-snug">{it.title}</div>
                        {(it.subtitle || it.meta) && (
                          <div className="text-[11px] font-mono uppercase tracking-wider mt-0.5" style={{ color: "var(--wofy-text-soft)" }}>
                            {[it.subtitle, it.meta].filter(Boolean).join(" · ")}
                          </div>
                        )}
                        {it.snippet && (
                          <p className="text-sm mt-1 line-clamp-2" style={{ color: "var(--wofy-text-soft)" }}>
                            {it.snippet}
                          </p>
                        )}
                      </div>
                      <ArrowRight className="w-4 h-4 opacity-40 mt-2 shrink-0" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </>
      )}

      {!loading && !data && q.length < 2 && (
        <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>
          Type at least 2 characters and hit search.
        </p>
      )}
    </div>
  );
}
